import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Search,
  PackageSearch,
  Loader2,
  AlertTriangle,
  TrendingDown,
  TrendingUp,
  Settings2,
  MoreHorizontal,
  ArrowUp,
  ArrowDown,
  ChevronsUpDown,
  Download,
  FileText,
  FileSpreadsheet,
  ChevronDown,
} from 'lucide-react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { usePermission } from '@/components/guards/permission-guard';
import { useAuthStore } from '@/stores/auth-store';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { StockAdjustmentModal } from '@/components/trading/stock-adjustment-modal';
import { trpc } from '@/lib/trpc';
import { showErrorToast } from '@/hooks/use-mutation-with-toast';
import { dateStamp } from '@/lib/download-xlsx';
import { buildStockLevelsDoc } from '@/lib/stock-levels-export';
import { exportTableReportXlsx } from '@/lib/report-table-xlsx';
import { downloadTableReportPdf } from '@/lib/report-table-pdf';

interface StockLevel {
  id: string;
  productId: string;
  productName: string;
  productCode: string;
  brandName?: string;
  modelName?: string;
  warehouseId: string;
  warehouseName: string;
  warehouseCode?: string;
  quantity: number;
  reserved: number;
  onOrder: number;
  averageCost?: number | null;
  lastCost?: number | null;
  reorderPoint?: number | null;
  reorderQty?: number | null;
  isLowStock?: boolean;
}

const stockStatusColors: Record<string, string> = {
  inStock: 'bg-green-100 text-green-700',
  lowStock: 'bg-amber-100 text-amber-700',
  outOfStock: 'bg-red-100 text-red-700',
  belowReorder: 'bg-orange-100 text-orange-700',
};

// Grid column layout shared between header and every row so they stay aligned
// under the virtualizer (which renders rows at absolute positions).
const GRID_COLS_BASE = '2fr 1.5fr 90px 90px 90px 120px 140px 110px';
const GRID_COLS_WITH_ACTIONS = `${GRID_COLS_BASE} 60px`;

// Only real persisted/related columns are sortable server-side. Available and
// stock value are derived (quantity − reserved, available × avg cost) so they
// cannot be ordered by the database without a generated column.
type InvSortKey = 'product' | 'warehouse' | 'reserved' | 'onOrder' | 'averageCost';
type SortDir = 'asc' | 'desc';

const PAGE_SIZE = 50;
const ROW_HEIGHT = 64;

export function StockLevelsPage() {
  const { t } = useTranslation('trading');
  const { t: tCommon } = useTranslation('common');
  const canEdit = usePermission('STOCK_LEVELS', 'EDIT');
  const user = useAuthStore((state) => state.user);
  const isAdmin = user?.role === 'ADMIN';
  const showActions = isAdmin && canEdit;

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedWarehouse, setSelectedWarehouse] = useState<string>('all');
  const [selectedBrand, setSelectedBrand] = useState<string>('all');
  const [selectedModel, setSelectedModel] = useState<string>('all');
  const [displayCurrency, setDisplayCurrency] = useState<'TRY' | 'EUR' | 'USD' | 'GBP'>('TRY');
  const [sortBy, setSortBy] = useState<InvSortKey | undefined>();
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  // Stored stock costs are in TRY (as of the fxRate freeze). Fetch today's
  // rates so we can convert to whatever currency the user picks for display.
  const [ratesAsOfDate] = useState(() => {
    const d = new Date();
    d.setHours(23, 59, 59, 999);
    return d;
  });
  const { data: ratesData } = trpc.exchangeRate.getAtDateAllRates.useQuery(
    { baseCurrency: 'TRY', date: ratesAsOfDate },
    { enabled: displayCurrency !== 'TRY' }
  );
  const ratesByFromCurrency = ratesData?.rates ?? {};
  const tryToDisplay = (tryValue: number | null | undefined): number | null => {
    if (tryValue === null || tryValue === undefined) return null;
    if (displayCurrency === 'TRY') return tryValue;
    const rate = ratesByFromCurrency[displayCurrency];
    if (!rate || rate <= 0) return null;
    return tryValue / rate;
  };
  const selectedRate = ratesByFromCurrency[displayCurrency];
  const missingRate =
    displayCurrency !== 'TRY' &&
    ratesData !== undefined &&
    !(typeof selectedRate === 'number' && selectedRate > 0);

  const utils = trpc.useUtils();
  const [exporting, setExporting] = useState(false);

  // Stock adjustment modal state
  const [adjustmentModalOpen, setAdjustmentModalOpen] = useState(false);
  const [selectedStockItem, setSelectedStockItem] = useState<{
    product: { id: string; name: string; code: string };
    warehouse: { id: string; name: string };
  } | null>(null);

  const handleOpenAdjustment = (level?: StockLevel) => {
    if (level) {
      setSelectedStockItem({
        product: { id: level.productId, name: level.productName, code: level.productCode },
        warehouse: { id: level.warehouseId, name: level.warehouseName },
      });
    } else {
      setSelectedStockItem(null);
    }
    setAdjustmentModalOpen(true);
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setTimeout(() => {
      setDebouncedSearch(value);
    }, 300);
  };

  const runExport = async (kind: 'pdf' | 'xlsx') => {
    setExporting(true);
    try {
      const exportData = await utils.stockLevel.exportList.fetch({
        search: debouncedSearch || undefined,
        warehouseId: selectedWarehouse !== 'all' ? selectedWarehouse : undefined,
        brandId: selectedBrand !== 'all' ? selectedBrand : undefined,
        modelId: selectedModel !== 'all' ? selectedModel : undefined,
        displayCurrency,
      });

      const paramsSummary: string[] = [];
      if (debouncedSearch) paramsSummary.push(`Arama: ${debouncedSearch}`);
      if (selectedWarehouse !== 'all') {
        const wh = warehouses.find((w) => w.id === selectedWarehouse);
        if (wh) paramsSummary.push(`Depo: ${wh.name}`);
      }
      if (selectedBrand !== 'all') {
        const br = brands.find((b) => b.id === selectedBrand);
        if (br) paramsSummary.push(`Marka: ${br.name}`);
      }
      if (selectedModel !== 'all') {
        const mdl = models.find((m) => m.id === selectedModel);
        if (mdl) paramsSummary.push(`Model: ${mdl.name}`);
      }

      const doc = buildStockLevelsDoc(exportData, paramsSummary);
      const fn = `trading-stok-seviyeleri-${dateStamp()}.${kind === 'pdf' ? 'pdf' : 'xlsx'}`;
      if (kind === 'pdf') {
        await downloadTableReportPdf(doc, fn);
      } else {
        await exportTableReportXlsx(doc, fn);
      }
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Export başarısız');
    } finally {
      setExporting(false);
    }
  };

  // Click a column header to sort by it; click again to flip direction.
  const toggleSort = (key: InvSortKey) => {
    if (sortBy === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortDir('asc');
    }
  };

  const { data: warehousesData } = trpc.warehouse.list.useQuery({});
  const { data: brandsData } = trpc.tradingBrand.list.useQuery({});
  // Models are scoped to the selected brand. When "all brands" we skip
  // loading models to avoid a huge dropdown — clear any picked model then.
  const { data: modelsData } = trpc.tradingModel.list.useQuery(
    { brandId: selectedBrand !== 'all' ? selectedBrand : undefined },
    { enabled: selectedBrand !== 'all' }
  );
  // Reset the model filter whenever the brand filter changes — a model from
  // the previous brand wouldn't match anything under the new one.
  useEffect(() => {
    setSelectedModel('all');
  }, [selectedBrand]);

  // Infinite / cursor-based query — tRPC v11 wires `cursor` to pageParam and
  // appends new pages to the accumulated cache. Each page carries nextCursor
  // (null when exhausted) that getNextPageParam returns to the runtime.
  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = trpc.stockLevel.list.useInfiniteQuery(
    {
      limit: PAGE_SIZE,
      search: debouncedSearch || undefined,
      warehouseId: selectedWarehouse !== 'all' ? selectedWarehouse : undefined,
      brandId: selectedBrand !== 'all' ? selectedBrand : undefined,
      modelId: selectedModel !== 'all' ? selectedModel : undefined,
      sortBy,
      sortDir,
    },
    {
      initialCursor: 1,
      getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    }
  );

  const warehouses = warehousesData ?? [];
  const brands = brandsData?.brands ?? [];
  const models = modelsData?.models ?? [];
  const stockLevels = useMemo<StockLevel[]>(
    () => data?.pages.flatMap((p) => p.stockLevels) ?? [],
    [data]
  );
  const totalRowCount = data?.pages[0]?.pagination.total ?? stockLevels.length;

  // Virtualizer — renders only the rows currently in view.
  const parentRef = useRef<HTMLDivElement>(null);
  const rowVirtualizer = useVirtualizer({
    count: stockLevels.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 10,
  });

  // Trigger fetch when the last rendered virtual item is close to the end
  // of the loaded set. Runs on every render the virtualizer re-fires.
  const virtualItems = rowVirtualizer.getVirtualItems();
  useEffect(() => {
    if (!hasNextPage || isFetchingNextPage) return;
    const last = virtualItems[virtualItems.length - 1];
    if (!last) return;
    if (last.index >= stockLevels.length - 10) {
      fetchNextPage();
    }
  }, [virtualItems, hasNextPage, isFetchingNextPage, stockLevels.length, fetchNextPage]);

  const getStockStatus = (level: StockLevel) => {
    const available = level.quantity - level.reserved;
    if (available <= 0) return 'outOfStock';
    if (level.reorderPoint && available <= level.reorderPoint) return 'belowReorder';
    if (available < 10) return 'lowStock';
    return 'inStock';
  };

  const getStockStatusLabel = (status: string) => {
    switch (status) {
      case 'inStock':
        return t('inventory.stockLevels.inStock');
      case 'lowStock':
        return t('inventory.stockLevels.lowStock');
      case 'outOfStock':
        return t('inventory.stockLevels.outOfStock');
      case 'belowReorder':
        return t('inventory.stockLevels.belowReorder');
      default:
        return status;
    }
  };

  const formatCurrency = (value: number | null | undefined) => {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: displayCurrency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const gridColumns = showActions ? GRID_COLS_WITH_ACTIONS : GRID_COLS_BASE;

  // Sortable column header — a real <button> for keyboard + screen-reader support.
  const renderSortHeader = (labelKey: string, key: InvSortKey, align: 'left' | 'right' = 'left') => {
    const label = t(`inventory.stockLevels.${labelKey}`);
    const active = sortBy === key;
    const ariaLabel = active
      ? t(
          sortDir === 'asc'
            ? 'inventory.stockLevels.sortedAscending'
            : 'inventory.stockLevels.sortedDescending',
          { column: label }
        )
      : t('inventory.stockLevels.sortBy', { column: label });
    return (
      <button
        type="button"
        onClick={() => toggleSort(key)}
        aria-label={ariaLabel}
        title={ariaLabel}
        className={`flex items-center gap-1 px-4 py-3 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-foreground/50 ${
          align === 'right' ? 'justify-end' : ''
        } ${active ? 'text-foreground' : ''}`}
      >
        <span className="truncate">{label}</span>
        {active ? (
          sortDir === 'asc' ? (
            <ArrowUp className="h-3 w-3 shrink-0" />
          ) : (
            <ArrowDown className="h-3 w-3 shrink-0" />
          )
        ) : (
          <ChevronsUpDown className="h-3 w-3 shrink-0 opacity-40" />
        )}
      </button>
    );
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t('inventory.stockLevels.title')}</h1>
          <p className="text-muted-foreground">
            {t('inventory.stockLevels.subtitle')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" disabled={exporting}>
                {exporting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Download className="mr-2 h-4 w-4" />
                )}
                {t('common.export', 'Dışa Aktar')}
                <ChevronDown className="ml-2 h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => runExport('pdf')}>
                <FileText className="mr-2 h-4 w-4 text-red-600" />
                {t('common.exportPdf', 'PDF')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => runExport('xlsx')}>
                <FileSpreadsheet className="mr-2 h-4 w-4 text-green-600" />
                {t('common.exportExcel', 'Excel')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {showActions && (
            <Button variant="outline" onClick={() => handleOpenAdjustment()}>
              <Settings2 className="mr-2 h-4 w-4" />
              {t('inventory.adjustment.title')}
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder={t('inventory.stockLevels.searchPlaceholder')}
            aria-label={t('inventory.stockLevels.searchAriaLabel')}
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="h-8 w-full border border-border bg-background pl-10 pr-4 text-sm focus:outline-none focus:border-foreground focus:ring-1 focus:ring-foreground/20"
          />
        </div>
        <Select value={selectedWarehouse} onValueChange={setSelectedWarehouse}>
          <SelectTrigger className="w-[200px]" aria-label={t('inventory.stockLevels.filterByWarehouse')}>
            <SelectValue placeholder={t('inventory.stockLevels.selectWarehouse')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('inventory.stockLevels.allWarehouses')}</SelectItem>
            {warehouses.map((wh) => (
              <SelectItem key={wh.id} value={wh.id}>
                {wh.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={selectedBrand} onValueChange={setSelectedBrand}>
          <SelectTrigger className="w-[180px]" aria-label={t('inventory.stockLevels.filterByBrand')}>
            <SelectValue placeholder={t('inventory.stockLevels.selectBrand')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('inventory.stockLevels.allBrands')}</SelectItem>
            {brands.map((b) => (
              <SelectItem key={b.id} value={b.id}>
                {b.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={selectedModel}
          onValueChange={setSelectedModel}
          disabled={selectedBrand === 'all'}
        >
          <SelectTrigger className="w-[180px]" aria-label={t('inventory.stockLevels.filterByModel')}>
            <SelectValue placeholder={t('inventory.stockLevels.selectModel')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('inventory.stockLevels.allModels')}</SelectItem>
            {models.map((m) => (
              <SelectItem key={m.id} value={m.id}>
                {m.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={displayCurrency}
          onValueChange={(v) => setDisplayCurrency(v as 'TRY' | 'EUR' | 'USD' | 'GBP')}
        >
          <SelectTrigger className="w-[120px]" aria-label={t('inventory.stockLevels.selectCurrency')}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="TRY">TRY</SelectItem>
            <SelectItem value="EUR">EUR</SelectItem>
            <SelectItem value="USD">USD</SelectItem>
            <SelectItem value="GBP">GBP</SelectItem>
          </SelectContent>
        </Select>
        {missingRate && (
          <span className="text-xs text-amber-600">
            No {displayCurrency}/TRY rate found — showing &ldquo;-&rdquo;
          </span>
        )}
        {displayCurrency !== 'TRY' && !missingRate && (
          // Costs are frozen TRY book values; a foreign-currency view is a
          // TRANSLATION at the current rate and will move with the market.
          <span className="text-xs text-muted-foreground">
            {t(
              'inventory.stockLevels.fxTranslationNote',
              'TL defter değerinin bugünkü kurla çevrimi — kur değiştikçe değişir'
            )}
          </span>
        )}
        {!isLoading && !error && stockLevels.length > 0 && (
          <span className="ml-auto text-xs text-muted-foreground tabular-nums">
            {stockLevels.length} / {totalRowCount}
          </span>
        )}
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <span className="ml-2 text-muted-foreground">{t('common.loading')}</span>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="border border-red-200 bg-red-50 p-4 text-red-700">
          <p className="font-medium">{t('common.error')}</p>
          <p className="text-sm">{error.message}</p>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !error && stockLevels.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <PackageSearch className="h-12 w-12 text-muted-foreground/50" />
          <h3 className="mt-4 text-lg font-medium">{t('inventory.stockLevels.noStock')}</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            {t('inventory.stockLevels.noStockDesc')}
          </p>
        </div>
      )}

      {/* Virtualized Stock Levels List */}
      {!isLoading && !error && stockLevels.length > 0 && (
        <div className="border border-border bg-card overflow-hidden">
          {/* Header (sticky-on-scroll via being outside the scroll container) */}
          <div
            role="row"
            className="grid bg-muted/50 text-sm font-medium border-b-2 border-b-border-strong"
            style={{ gridTemplateColumns: gridColumns }}
          >
            {renderSortHeader('product', 'product')}
            {renderSortHeader('warehouse', 'warehouse')}
            <div className="px-4 py-3 text-right">{t('inventory.stockLevels.available')}</div>
            {renderSortHeader('reserved', 'reserved', 'right')}
            {renderSortHeader('onOrder', 'onOrder', 'right')}
            {renderSortHeader('averageCost', 'averageCost', 'right')}
            <div className="px-4 py-3 text-right">{t('inventory.stockLevels.stockValue')}</div>
            <div className="px-4 py-3 text-center">{t('inventory.stockLevels.status')}</div>
            {showActions && <div className="px-4 py-3 text-center">{tCommon('actions')}</div>}
          </div>

          {/* Scroll container — the virtualizer places rows absolutely inside
              a div whose height equals the virtual total size. */}
          <div
            ref={parentRef}
            className="overflow-auto"
            style={{ height: 'calc(100vh - 320px)', minHeight: 400 }}
          >
            <div
              style={{
                height: rowVirtualizer.getTotalSize(),
                width: '100%',
                position: 'relative',
              }}
            >
              {virtualItems.map((virtualRow) => {
                const level = stockLevels[virtualRow.index]!;
                const status = getStockStatus(level);
                const available = level.quantity - level.reserved;
                const stockValueTRY = level.averageCost ? available * level.averageCost : null;
                const averageCostDisplay = tryToDisplay(level.averageCost);
                const stockValueDisplay = tryToDisplay(stockValueTRY);

                return (
                  <div
                    key={level.id}
                    role="row"
                    className="grid items-center hover:bg-muted/30 border-b border-border text-sm absolute top-0 left-0 w-full"
                    style={{
                      gridTemplateColumns: gridColumns,
                      height: virtualRow.size,
                      transform: `translateY(${virtualRow.start}px)`,
                    }}
                  >
                    <div className="px-4 py-2">
                      <div className="font-medium truncate">{level.productName}</div>
                      <div className="text-xs text-muted-foreground truncate">{level.productCode}</div>
                    </div>
                    <div className="px-4 py-2 truncate">{level.warehouseName}</div>
                    <div className="px-4 py-2 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {status === 'lowStock' && (
                          <TrendingDown className="h-4 w-4 text-amber-500" />
                        )}
                        {status === 'outOfStock' && (
                          <AlertTriangle className="h-4 w-4 text-red-500" />
                        )}
                        <span className="font-medium tabular-nums">{available}</span>
                      </div>
                    </div>
                    <div className="px-4 py-2 text-right tabular-nums">{level.reserved}</div>
                    <div className="px-4 py-2 text-right">
                      <div className="flex items-center justify-end gap-2 tabular-nums">
                        {level.onOrder > 0 && (
                          <TrendingUp className="h-4 w-4 text-blue-500" />
                        )}
                        <span>{level.onOrder}</span>
                      </div>
                    </div>
                    <div className="px-4 py-2 text-right tabular-nums">
                      {formatCurrency(averageCostDisplay)}
                    </div>
                    <div className="px-4 py-2 text-right tabular-nums font-medium">
                      {formatCurrency(stockValueDisplay)}
                    </div>
                    <div className="px-4 py-2 text-center">
                      <span
                        className={`inline-block px-2 py-0.5 text-xs font-medium ${stockStatusColors[status]}`}
                      >
                        {getStockStatusLabel(status)}
                      </span>
                    </div>
                    {showActions && (
                      <div className="px-4 py-2 text-center">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              aria-label={tCommon('actions')}
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleOpenAdjustment(level)}>
                              <Settings2 className="mr-2 h-4 w-4" />
                              {t('inventory.adjustment.adjustStock')}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Loader for the next page — appears at the bottom of the virtual
                space. The effect above triggers fetchNextPage when the user
                scrolls within 10 rows of the end. */}
            {isFetchingNextPage && (
              <div className="flex items-center justify-center py-4 text-sm text-muted-foreground">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('common.loading')}
              </div>
            )}
            {!hasNextPage && stockLevels.length >= PAGE_SIZE && (
              <div className="py-3 text-center text-xs text-muted-foreground">
                {tCommon('endOfList', { defaultValue: 'End of list' })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Stock Adjustment Modal */}
      <StockAdjustmentModal
        open={adjustmentModalOpen}
        onOpenChange={setAdjustmentModalOpen}
        preselectedProduct={selectedStockItem?.product}
        preselectedWarehouse={selectedStockItem?.warehouse}
      />
    </div>
  );
}
